import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import cx from "classnames";
import { sendMessageToParent } from "@/utils/messaging";
import { SvgIcon } from "../SvgIcon";
import { useConfigStore } from "@/store/useConfigStore";
import { MODE_FLOAT } from "@/config";
import logoAMLBot from "@/assets/images/logo-amlbot.svg";

import styles from "./Layout.module.sass";

interface LayoutProps {
  title?: string;
  children: ReactNode;
  isShowBackButton?: boolean;
  backButtonText?: string;
  isShowCloseButton?: boolean;
  renderFooter?: ReactNode;
  onBack?: () => void;
}

const Layout = ({
  title,
  children,
  isShowBackButton = false,
  isShowCloseButton = true,
  renderFooter,
  onBack,
  backButtonText,
}: LayoutProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const widgetOrigin = useConfigStore((state) => state.parentOrigin);
  const widgetMode = useConfigStore((state) => state.config.widgetMode);

  const handleClose = useCallback(() => {
    sendMessageToParent("CLOSE_WIDGET", {}, widgetOrigin);
  }, [widgetOrigin]);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    const handleScroll = () => {
      setIsScrolled(element.scrollTop > 0);
    };

    element.addEventListener("scroll", handleScroll);

    return () => {
      element.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const classes = cx(styles.layout, {
    [styles["is-scrolled"]]: isScrolled,
  });

  const closeButtonVisible = isShowCloseButton && widgetMode === MODE_FLOAT;

  return (
    <div className={classes}>
      <div className={styles.header}>
        {isShowBackButton && (
          <button type="button" className={styles.back} onClick={onBack}>
            <SvgIcon name="arrow-left-short" />
            {backButtonText || ""}
          </button>
        )}

        {title && <div className={styles.title}>{title}</div>}

        {closeButtonVisible && (
          <button type="button" className={styles.close} onClick={handleClose}>
            <SvgIcon name="close" />
          </button>
        )}
      </div>

      <div ref={contentRef} className={styles.content}>
        {children}
      </div>

      <div className={styles.footer}>
        {renderFooter}

        <div className={styles.powered}>
          Powered by <img src={logoAMLBot} alt="AMLBot" />
        </div>
      </div>
    </div>
  );
};

export default Layout;
